// Accessibility preferences — font scale plus a complete set of
// UserWay-style toggles. All persisted to localStorage via vuex-persist.

import i18n from '@/i18n';

const MIN_SCALE = 0.85;
const MAX_SCALE = 1.5;
const STEP = 0.05;

const clamp = (v) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, Number(v.toFixed(2))));

const defaultFeatures = () => ({
  // Text size (also driven by manual +/- and slider)
  biggerText: false,

  // Visual
  contrastPlus: 'off', // 'off' | 'high' | 'dark' | 'light'
  saturation: 'normal', // 'normal' | 'low' | 'high' | 'mono'
  hideImages: false,
  pauseAnimations: false,

  // Links / targets
  highlightLinks: false,

  // Typography
  dyslexiaFriendly: false,
  textSpacing: 'normal', // 'normal' | 'light' | 'moderate' | 'heavy'
  lineHeight: 'normal', // 'normal' | 'tall' | 'taller'
  textAlign: 'default', // 'default' | 'left' | 'center' | 'right' | 'justify'

  // UI helpers
  bigCursor: false,
  tooltipsVisible: false,
  keyboardNav: false,

  // Advanced input-assistance
  onScreenKeyboard: false,
  dwellClick: false,
  dwellDelay: 1200, // ms — customisable in the panel
  stickyKeys: false,
  slowKeys: false,
  slowKeysDelay: 400, // ms
  typingSuggestions: false,

  // Text-to-speech (WCAG 2.1 SC 1.1.1 — spoken text alternatives).
  // When on, clicking any text element reads its contents aloud via
  // the browser's speechSynthesis API.
  screenReader: false
});

export const state = () => ({
  fontScale: 1,
  // Off by default: the user opts in from the Accessibility panel. Persisted under a new key
  // because this module is saved to localStorage, and returning visitors still carry the old
  // `autoAdjustEnabled: true` default, which would otherwise be restored over this one.
  autoAdjustOptIn: false,
  autoAdjustedAt: null,
  lastAutoAdjustMessage: null,
  features: defaultFeatures()
});

export const getters = {
  getFontScale: (state) => state.fontScale,
  getAutoAdjustEnabled: (state) => state.autoAdjustOptIn,
  getLastAutoAdjustMessage: (state) => state.lastAutoAdjustMessage,
  getFeatures: (state) => state.features,
  minScale: () => MIN_SCALE,
  maxScale: () => MAX_SCALE,
  stepSize: () => STEP,
  // Count how many features are active — surfaces a badge on the FAB
  activeFeatureCount: (state) => {
    const f = state.features;
    let n = 0;
    if (state.fontScale !== 1) n++;
    if (f.biggerText) n++;
    if (f.contrastPlus !== 'off') n++;
    if (f.saturation !== 'normal') n++;
    if (f.hideImages) n++;
    if (f.pauseAnimations) n++;
    if (f.highlightLinks) n++;
    if (f.dyslexiaFriendly) n++;
    if (f.textSpacing !== 'normal') n++;
    if (f.lineHeight !== 'normal') n++;
    if (f.textAlign !== 'default') n++;
    if (f.bigCursor) n++;
    if (f.tooltipsVisible) n++;
    if (f.keyboardNav) n++;
    if (f.onScreenKeyboard) n++;
    if (f.dwellClick) n++;
    if (f.stickyKeys) n++;
    if (f.slowKeys) n++;
    if (f.typingSuggestions) n++;
    if (f.screenReader) n++;
    return n;
  }
};

export const mutations = {
  setFontScale(state, scale) {
    state.fontScale = clamp(scale);
  },
  setAutoAdjustEnabled(state, enabled) {
    state.autoAdjustOptIn = !!enabled;
  },
  setAutoAdjustedAt(state, timestamp) {
    state.autoAdjustedAt = timestamp;
  },
  setLastAutoAdjustMessage(state, message) {
    state.lastAutoAdjustMessage = message;
  },
  resetFontScale(state) {
    state.fontScale = 1;
  },
  // Generic feature setter — key/value pair
  setFeature(state, { key, value }) {
    if (state.features[key] === undefined) return;
    state.features = { ...state.features, [key]: value };
  },
  resetAllFeatures(state) {
    state.features = defaultFeatures();
    state.fontScale = 1;
  }
};

export const actions = {
  setFontScale({ commit }, scale) {
    commit('setFontScale', scale);
  },
  increaseFontScale({ commit, state }) {
    commit('setFontScale', state.fontScale + STEP);
  },
  decreaseFontScale({ commit, state }) {
    commit('setFontScale', state.fontScale - STEP);
  },
  resetFontScale({ commit }) {
    commit('resetFontScale');
  },
  setAutoAdjustEnabled({ commit }, enabled) {
    commit('setAutoAdjustEnabled', enabled);
  },
  setFeature({ commit }, payload) {
    commit('setFeature', payload);
  },
  toggleFeature({ commit, state }, key) {
    commit('setFeature', { key, value: !state.features[key] });
  },
  cycleFeature({ commit, state }, { key, values }) {
    // Moves through an array of allowed values (for multi-state toggles)
    const current = state.features[key];
    const idx = values.indexOf(current);
    const next = values[(idx + 1) % values.length];
    commit('setFeature', { key, value: next });
  },
  resetAllAccessibility({ commit }) {
    commit('resetAllFeatures');
  },
  autoBumpFontScale({ commit, state }, reason) {
    if (!state.autoAdjustOptIn) return false;
    if (state.fontScale >= MAX_SCALE) return false;
    commit('setFontScale', state.fontScale + STEP);
    commit('setAutoAdjustedAt', Date.now());
    commit(
      'setLastAutoAdjustMessage',
      reason || i18n.t('Services.FontSizeAutoAdjust.default')
    );
    return true;
  },
  clearAutoAdjustMessage({ commit }) {
    commit('setLastAutoAdjustMessage', null);
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
